import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, LogOut, RefreshCw, RotateCcw, XCircle, FileText, Clock, MapPin, Loader2, ShoppingBag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { fetchMyOrders, cancelMyOrder } from "../lib/api";
import { inr } from "../lib/format";
import AddressBook from "../components/AddressBook";

const FIRM = {
  name: "Global Treats Trading LLP",
  gst: "09ABBFG6961G1Z7",
  address: "15th Floor, Max Burger Tower, Sector-16B, Noida, Gautam Buddha Nagar - 201301",
  mobile: "+91 92649 71235",
  email: "globaltreatstradingllp@gmail.com",
  website: "www.Globaltreats.in",
};

const GST_RATE = 0.05;

const STATUS_STYLES = {
  "Received": "bg-[#FCEFE3] text-[#B07A5C]",
  "Baking": "bg-[#FDF0D5] text-[#B8860B]",
  "Out for Delivery": "bg-[#E0EEFB] text-[#2A6FB0]",
  "Delivered": "bg-[#E7F3E0] text-[#4f8a2f]",
  "Cancellation Requested": "bg-[#FBE0E0] text-[#C0392B]",
  "Cancelled": "bg-[#EEE] text-[#777]",
};

const canCancel = (order) => {
  if (["Cancelled", "Cancellation Requested", "Delivered", "Out for Delivery"].includes(order.status)) return false;
  const created = new Date(order.created_at);
  return Date.now() - created.getTime() <= 60 * 60 * 1000;
};

const buildInvoiceHtml = (order) => {
  const rows = order.items
    .map(
      (i) => `<tr>
        <td>${i.name}<br/><span style="color:#888;font-size:12px">${i.flavor} · ${i.weight}${i.message ? " · Msg: " + i.message : ""}</span></td>
        <td style="text-align:center">${i.qty}</td>
        <td style="text-align:right">₹${Number(i.price).toLocaleString("en-IN")}</td>
        <td style="text-align:right">₹${Number(i.price * i.qty).toLocaleString("en-IN")}</td>
      </tr>`
    )
    .join("");
  const inrFmt = (n) => "₹" + Number(Math.round(n)).toLocaleString("en-IN");
  const taxable = Math.round(Number(order.total) || 0); // subtotal + delivery
  const gstTotal = Math.round(taxable * GST_RATE);
  const cgst = Math.round(gstTotal / 2);
  const sgst = gstTotal - cgst;
  const grand = taxable + gstTotal;
  const logoUrl = `${window.location.origin}/assets/globaltreats-gift-logo.webp`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Tax Invoice ${order.id}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:'Segoe UI',Arial,Helvetica,sans-serif;color:#2b2b2b;max-width:800px;margin:0 auto;padding:32px 28px;background:#fff}
      .brand{text-align:center;margin-bottom:6px}
      .brand img{width:110px;height:110px;object-fit:contain;margin:0 auto}
      .firm-name{font-size:20px;font-weight:700;color:#3A2417;margin-top:2px;letter-spacing:.5px}
      .firm-gst{font-size:12px;color:#8a6d5a;margin-top:2px}
      .title-bar{margin:20px 0 6px;background:linear-gradient(90deg,#E8C560,#D4A017,#E8C560);color:#3A2417;text-align:center;padding:11px;border-radius:8px;font-size:22px;font-weight:800;letter-spacing:5px}
      .divider{height:2px;background:#D4A017;opacity:.35;margin:0 0 18px}
      .meta{display:flex;justify-content:space-between;gap:20px;font-size:13px;margin-bottom:8px}
      .meta .box{background:#FCF7EA;border:1px solid #EEDFB8;border-radius:10px;padding:12px 14px;flex:1;line-height:1.6}
      .meta .label{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#B8860B;font-weight:700;margin-bottom:4px}
      table{width:100%;border-collapse:collapse;margin-top:18px;border:1px solid #EEDFB8;border-radius:10px;overflow:hidden}
      th{background:#D4A017;color:#fff;text-align:left;padding:11px 12px;font-size:12px;letter-spacing:.5px}
      td{padding:11px 12px;border-bottom:1px solid #F0E7D0;font-size:13px}
      tbody tr:last-child td{border-bottom:none}
      tbody tr:nth-child(even){background:#FCF9F1}
      .totals{margin-top:16px;margin-left:auto;width:300px;font-size:14px;background:#FCF7EA;border:1px solid #EEDFB8;border-radius:10px;padding:14px 16px}
      .totals div{display:flex;justify-content:space-between;padding:4px 0}
      .totals .muted{color:#7a6a58;font-size:13px}
      .grand{border-top:2px dashed #D4A017;margin-top:8px;padding-top:10px;font-weight:800;font-size:18px;color:#B8860B}
      .footer{clear:both;margin-top:34px;border-top:2px solid #D4A017;padding-top:14px;font-size:12px;color:#555;line-height:1.8;text-align:center}
      .footer b{color:#3A2417}
      .note{text-align:center;color:#aaa;font-size:11px;margin-top:14px}
      .badge{display:inline-block;background:#fff;border:1px solid #D4A017;color:#B8860B;padding:3px 12px;border-radius:20px;font-size:11px;font-weight:600}
    </style></head><body>
    <div class="brand">
      <img src="${logoUrl}" alt="Globaltreats" onerror="this.style.display='none'"/>
      <div class="firm-name">${FIRM.name}</div>
      <div class="firm-gst">GSTIN: ${FIRM.gst}</div>
    </div>
    <div class="title-bar">TAX INVOICE</div>
    <div class="divider"></div>
    <div class="meta">
      <div class="box"><div class="label">Bill To</div>${order.name}<br/>${order.phone}<br/>${order.address}, ${order.city} - ${order.pincode}</div>
      <div class="box" style="text-align:right"><div class="label">Invoice Details</div><b>Invoice No:</b> ${order.id}<br/><b>Order Date:</b> ${new Date(order.created_at).toLocaleString("en-IN")}<br/><b>Delivery:</b> ${order.date} (${order.slot})<br/><span class="badge">${order.status}</span></div>
    </div>
    <table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="totals">
      <div class="muted"><span>Subtotal</span><span>${inrFmt(order.subtotal)}</span></div>
      <div class="muted"><span>Delivery</span><span>${order.delivery_fee ? inrFmt(order.delivery_fee) : "FREE"}</span></div>
      <div><span>Taxable Value</span><span>${inrFmt(taxable)}</span></div>
      <div class="muted"><span>CGST @ 2.5%</span><span>${inrFmt(cgst)}</span></div>
      <div class="muted"><span>SGST @ 2.5%</span><span>${inrFmt(sgst)}</span></div>
      <div class="grand"><span>Grand Total</span><span>${inrFmt(grand)}</span></div>
    </div>
    <div class="footer">
      <b>Address:</b> ${FIRM.address}<br/>
      <b>Mobile:</b> ${FIRM.mobile} &nbsp;•&nbsp; <b>Email:</b> ${FIRM.email} &nbsp;•&nbsp; <b>Website:</b> ${FIRM.website}
    </div>
    <div class="note">Thank you for ordering from Globaltreats! · This is a computer-generated tax invoice.</div>
    </body></html>`;
};

const MyOrders = () => {
  const { user, logout, openLogin } = useAuth();
  const { addItem, setIsOpen } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await fetchMyOrders();
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
      } else {
        setError("Could not load your orders. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user, load]);

  const handleReorder = (order) => {
    order.items.forEach((i) => addItem(i, { flavor: i.flavor, weight: i.weight, qty: i.qty, message: i.message }));
    setIsOpen(true);
  };

  const handleCancel = async (order) => {
    if (!window.confirm(`Request cancellation for order ${order.id}?`)) return;
    setBusyId(order.id);
    try {
      const res = await cancelMyOrder(order.id);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: res.status } : o)));
    } catch (err) {
      alert(err.response?.data?.detail || "Could not cancel this order.");
    } finally {
      setBusyId("");
    }
  };

  const handleInvoice = (order) => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(buildInvoiceHtml(order));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="my-orders-login-gate">
        <div className="w-20 h-20 rounded-full bg-[#FCEFE3] grid place-items-center mx-auto mb-6"><Package size={38} className="text-[#D46A8A]" /></div>
        <h1 className="font-display text-3xl font-bold text-[#3A2417]">Your Orders</h1>
        <p className="text-[#B07A5C] mt-3">Please login with WhatsApp to view your order history, track status and download invoices.</p>
        <button onClick={() => openLogin("/my-orders")} className="mt-6 h-12 px-8 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1eb956] transition-colors" data-testid="my-orders-login-btn">
          Login with WhatsApp
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="my-orders-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#3A2417]">My Orders</h1>
          <p className="text-[#B07A5C] text-sm mt-1">Hi {user.name || user.phone}, here are your orders.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="w-10 h-10 grid place-items-center rounded-full bg-white border border-[#EADBCB] hover:bg-[#F6E7DA]" title="Refresh" data-testid="my-orders-refresh-btn">
            <RefreshCw size={18} className="text-[#6B4226]" />
          </button>
          <button onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#6B4226] text-white text-sm hover:bg-[#5a3620]" data-testid="my-orders-logout-btn">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      <AddressBook />

      {loading ? (
        <div className="py-20 grid place-items-center text-[#B07A5C]"><Loader2 size={30} className="animate-spin" /></div>
      ) : error ? (
        <p className="text-center text-red-600 py-16">{error}</p>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[#F6E7DA] grid place-items-center mx-auto mb-4"><ShoppingBag size={32} className="text-[#D46A8A]" /></div>
          <p className="text-[#6B4226] font-medium">No orders yet</p>
          <button onClick={() => navigate("/collections/all")} className="mt-5 h-11 px-6 rounded-full bg-[#D46A8A] text-white text-sm hover:bg-[#c25a7b]">Browse Cakes</button>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-2xl bakery-shadow p-5" data-testid={`order-card-${order.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F1E4D6] pb-4">
                <div>
                  <div className="font-display text-lg font-bold text-[#D46A8A]">{order.id}</div>
                  <div className="text-xs text-[#B07A5C] flex items-center gap-1 mt-0.5"><Clock size={12} /> {new Date(order.created_at).toLocaleString("en-IN")}</div>
                </div>
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${STATUS_STYLES[order.status] || "bg-[#FCEFE3] text-[#B07A5C]"}`} data-testid={`order-status-${order.id}`}>
                  {order.status}
                </span>
              </div>

              <div className="py-4 space-y-3">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex gap-3">
                    <img src={i.image || "/assets/globaltreats-logo.jpeg"} alt={i.name} onError={(e) => { e.target.src = "/assets/globaltreats-logo.jpeg"; }} className="w-14 h-14 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#3A2417] line-clamp-1">{i.name}</div>
                      <div className="text-xs text-[#B07A5C]">{i.flavor} · {i.weight} × {i.qty}</div>
                    </div>
                    <div className="text-sm font-medium text-[#3A2417]">{inr(i.price * i.qty)}</div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-[#B07A5C] flex items-start gap-1.5 border-t border-[#F1E4D6] pt-3">
                <MapPin size={13} className="mt-0.5 shrink-0" />
                <span>Deliver to {order.address}, {order.city} - {order.pincode} · {order.date} ({order.slot})</span>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                <div className="font-display text-lg font-bold text-[#3A2417]">Total: {inr(order.total)}</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleInvoice(order)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#FCEFE3] text-[#6B4226] text-sm hover:bg-[#f6e0cf]" data-testid={`order-invoice-btn-${order.id}`}>
                    <FileText size={15} /> Tax Invoice
                  </button>
                  <button onClick={() => handleReorder(order)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#D46A8A] text-white text-sm hover:bg-[#c25a7b]" data-testid={`order-reorder-btn-${order.id}`}>
                    <RotateCcw size={15} /> Re-order
                  </button>
                  {canCancel(order) && (
                    <button onClick={() => handleCancel(order)} disabled={busyId === order.id} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white border border-[#F0C0C0] text-[#C0392B] text-sm hover:bg-[#FBE0E0] disabled:opacity-60" data-testid={`order-cancel-btn-${order.id}`}>
                      {busyId === order.id ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />} Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
